import get from 'lodash/get';
import forEach from 'lodash/forEach';
import keyBy from 'lodash/keyBy';
import flatten from 'lodash/flatten';

import { caseLogicMsg, dialogueMsg } from '../validationMessages';
import { getAllCharacters } from '~lib/common/utils/characters';
import { activeNpcs, getNpcDialogue } from '~lib/common/utils/npc';

function validateDialogueControls(caseConf, nodeInfo) {
  const questions = get(caseConf, 'dialogue.questions', []);
  const commands = get(caseConf, 'dialogue.commands', []);
  const unsolicited = get(caseConf, 'dialogue.unsolicited', []);
  const callItems = flatten((get(caseConf, 'dialogue.calls') || []).map(call => call.items))
  const callUnsolicited = flatten((get(caseConf, 'dialogue.calls') || []).map(call => call.unsolicited))
  const questionsById = keyBy(questions, 'id');
  const commandsById = keyBy(commands, 'id');
  const unsolicitedById = keyBy(unsolicited, 'id');
  const callItemsById = keyBy(callItems, 'id');
  const callUnsolicitedById = keyBy(callUnsolicited, 'id');
  const npcQuestions = getNpcDialogue(caseConf).questions || [];
  const npcCommands = getNpcDialogue(caseConf).commands || [];
  const npcUnsolicited = getNpcDialogue(caseConf).unsolicited || [];
  const npcUnsolicitedById = keyBy(npcUnsolicited, 'id');
  const npcQuestionsById = keyBy(npcQuestions, 'id');
  const npcCommandsById = keyBy(npcCommands, 'id');
  const characters = getAllCharacters(caseConf);
  const charactersByKey = keyBy(characters, 'key');

  const errors = [];
  const warnings = [];

  const dialogueItemsByTypeById = {
    QUESTION: questionsById,
    ANSWER: questionsById,
    COMMAND: commandsById,
    COMMAND_COUNT: commandsById,
    UNSOLICITED_DIALOGUE: unsolicitedById,
    UNSOLICITED_CALL: callUnsolicitedById,
    CALL: callItemsById,
    CALL_ANSWER: callItemsById,
    NPC_UNSOLICITED_DIALOGUE: npcUnsolicitedById,
    NPC_QUESTION: npcQuestionsById,
    NPC_COMMAND: npcCommandsById,
    NPC_COMMAND_COUNT: npcCommandsById,
    NPC_ANSWER: npcQuestionsById,
  };
  const dialogueTitleByType = {
    QUESTION : 'Question',
    ANSWER: 'Question',
    COMMAND: 'Command',
    COMMAND_COUNT: 'Command',
    UNSOLICITED_DIALOGUE: 'Unsolicited',
    UNSOLICITED_CALL: 'Unsolicited',
    CALL: 'Call dialogue',
    CALL_ANSWER: 'Call dialogue',
    NPC_UNSOLICITED_DIALOGUE: 'NPC Unsolicited',
    NPC_QUESTION: 'NPC Question',
    NPC_COMMAND: 'NPC Command',
    NPC_COMMAND_COUNT: 'NPC Command',
    NPC_ANSWER: 'NPC Question',
  };
  const dialogueArticleByType = {
    QUESTION : 'a',
    ANSWER: 'a',
    COMMAND: 'a',
    COMMAND_COUNT: 'a',
    UNSOLICITED_DIALOGUE: 'an',
    UNSOLICITED_CALL: 'an',
    CALL: 'a',
    CALL_ANSWER: 'a',
    NPC_UNSOLICITED_DIALOGUE: 'an',
    NPC_QUESTION: 'an',
    NPC_COMMAND: 'an',
    NPC_COMMAND_COUNT: 'an',
    NPC_ANSWER: 'an',
  };

  const npcs = activeNpcs(caseConf);
  const npcsByKey = keyBy(npcs, 'key');

  // Action nodes
  const dialogueActionTypes = ['QUESTION', 'COMMAND', 'CALL', 'NPC_QUESTION', 'NPC_COMMAND'];

  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && dialogueActionTypes.includes(action.type)) {
      const dialogueTitle = dialogueTitleByType[action.type];
      const dialogueArticle = dialogueArticleByType[action.type];
      if (!action.id) {
        errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase().replace('npc', 'NPC')}`, node.id, { errorId: 'missingQuestion' }));
        return;
      }
      const dialogueItem = dialogueItemsByTypeById[action.type][action.id];
      if (!dialogueItem) {
        errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { errorId: 'invalidQuestion' }));
      }
    }
  });

  const dialogueActionTypesWithCharacter = ['NPC_QUESTION', 'NPC_COMMAND']; 

  nodeInfo.actionNodes.forEach((node) => {
    const action = get(node, 'data.action');
    if (action && dialogueActionTypesWithCharacter.includes(action.type)) {
      if (!action.npcKey) {
        errors.push(caseLogicMsg('You need to select an NPC', node.id, { errorId: 'missingSourceCharacter' }));
        return;
      }
      if (action.npcKey && !npcsByKey[action.npcKey]) {
        errors.push(caseLogicMsg('You need to select an NPC', node.id, { errorId: 'missingSourceCharacter' }));
        return;
      }
    }
  });

  // Effect nodes
  const dialogueEffectTypes = ['QUESTION', 'ANSWER', 'COMMAND', 'CALL', 'CALL_ANSWER', 'UNSOLICITED_DIALOGUE', 'UNSOLICITED_CALL', 'NPC_QUESTION', 'NPC_COMMAND', 'NPC_ANSWER', 'NPC_UNSOLICITED_DIALOGUE'];
  const dialogueEffectTypesWithCharacter = ['NPC_QUESTION', 'NPC_COMMAND', 'NPC_ANSWER', 'NPC_UNSOLICITED_DIALOGUE']; 
  const usedUnsolicitedIds = [];

  nodeInfo.effectNodes.forEach((node) => {
    const effect = get(node, 'data.effect');
    if (effect && dialogueEffectTypes.includes(effect.type)) {
      const dialogueTitle = dialogueTitleByType[effect.type];
      const dialogueArticle = dialogueArticleByType[effect.type];
      if (!effect.id) {
        errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase()}`, node.id, { errorId: 'missingQuestion' }));
        return;
      }
      const dialogueItem = dialogueItemsByTypeById[effect.type][effect.id];
      if (!dialogueItem) {
        errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { errorId: 'invalidQuestion' }));
      } else if (effect.type === 'UNSOLICITED_DIALOGUE') {
        if (!dialogueItem.options.answer || !dialogueItem.options.answer.en) {
          errors.push(dialogueMsg('Empty unsolicited item', dialogueItem.id, { errorId: 'emptyUnsolicited', dialogueType: 'UNSOLICITED' }));
        }
      }
      if (dialogueEffectTypesWithCharacter.includes(effect.type)) {
        if (!effect.npcKey) {
          errors.push(caseLogicMsg('You need to select an NPC', node.id, { errorId: 'missingSourceCharacter' }));
          return;
        }
        if (effect.npcKey && !npcsByKey[effect.npcKey]) {
          errors.push(caseLogicMsg('You need to select an NPC', node.id, { errorId: 'missingSourceCharacter' }));
          return;
        }
        if (effect.type === 'NPC_UNSOLICITED_DIALOGUE') {
          usedUnsolicitedIds.push(effect.id)
          if (!effect.targetCharacterKey) {
            errors.push(caseLogicMsg('You need to select a Target Character', node.id, { errorId: 'missingTargetCharacter' }));
          }
        }
      }
      if (effect.type === 'UNSOLICITED_DIALOGUE') {
        if (!effect.targetCharacterKey) {
          errors.push(caseLogicMsg('You need to select a Target Character', node.id, { errorId: 'missingTargetCharacter' }));
        }
        if (effect.targetCharacterKey && !charactersByKey[effect.targetCharacterKey]) {
          errors.push(caseLogicMsg('Target character could not be found', node.id, { errorId: 'missingTargetCharacter' }));
        }
      }
    }
  });

  


  // Check nodes
  const dialogueCheckTypes = ['QUESTION', 'COMMAND_COUNT', 'UNSOLICITED_DIALOGUE', 'CALL', 'UNSOLICITED_DIALOGUE', 'UNSOLICITED_CALL', 'NPC_UNSOLICITED_DIALOGUE', 'NPC_QUESTION', 'NPC_COMMAND_COUNT'];
  const dialogueCheckTypesWithCharacter = ['NPC_UNSOLICITED_DIALOGUE', 'NPC_QUESTION', 'NPC_COMMAND_COUNT'];

  nodeInfo.checkNodes.forEach((node) => {
    forEach(node.data, (check, checkKey) => {
      if (dialogueCheckTypes.includes(check.type)) {
        const dialogueTitle = dialogueTitleByType[check.type];
        const dialogueArticle = dialogueArticleByType[check.type];
        if (!check.id) {
          errors.push(caseLogicMsg(`You need to select ${dialogueArticle} ${dialogueTitle.toLowerCase()}`, node.id, { checkKey }));
          return;
        }
        const dialogueItem = dialogueItemsByTypeById[check.type][check.id];
        if (!dialogueItem) {
          errors.push(caseLogicMsg(`${dialogueTitle} could not be found`, node.id, { checkKey }));
        }
        if (dialogueCheckTypesWithCharacter.includes(check.type)) {
          if (!check.npcKey) {
            errors.push(caseLogicMsg('You need to select an NPC', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
            return;
          }
          if (check.npcKey && !npcsByKey[check.npcKey]) {
            errors.push(caseLogicMsg('You need to select an NPC', node.id, { checkKey, errorId: 'missingSourceCharacter' }));
            return;
          }
        }
        if (check.type === 'NPC_UNSOLICITED_DIALOGUE' && !usedUnsolicitedIds.includes(check.id)) {  
          errors.push(caseLogicMsg(`You must first use this ${dialogueTitle} in an Effect`, node.id, { checkKey, errorId: 'invalidUnsolicited', checkId: check.id }));
        }
      }
    });
  });

  return {
    errors,
    warnings,
  };
}

export default validateDialogueControls;
